import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../../context/LanguageContext';
import { useThemeMode } from '../../../../context/ThemeContext';
import { getToggleLanguageInfo } from '../../../../data/types/LanguageTypes';
import { ROUTES } from '../../../routes/Paths';
import { query, where, onSnapshot } from 'firebase/firestore';
import { getClientCollection } from '../../../../services/FirebaseService';
import '../style/navbar.css';
import * as navbarScript from '../script/navbar';
import logoMain from '../../../../assets/logo/logo/main.webp';
import logoAlternative from '../../../../assets/logo/logo/alternative.webp';
import { trackEmailClick, trackPhoneClick, trackWhatsAppClick } from '../../email/utils/trackingUtils';

const Navbar: React.FC = () => {
    const { language, setLanguage, t } = useLanguage();
    const { mode, toggleColorMode } = useThemeMode();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
    const [wishlistCount, setWishlistCount] = useState(0);
    const [isNavbarVisible, setIsNavbarVisible] = useState(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const [topBarSchedule, setTopBarSchedule] = useState<string>('');
    const sidebarRef = useRef<HTMLDivElement>(null);
    const underlineRef = useRef<HTMLDivElement>(null);
    const activeLinkRef = useRef<HTMLElement | null>(null);
    const lastScrollY = useRef(0);
    const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

    // Handle search button click
    const handleSearchClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        document.body.classList.add('search-active');
        
        // Focus on search input after popup opens
        setTimeout(() => {
            const input = document.querySelector('.search-popup input[name="search-field"]') as HTMLInputElement;
            if (input) {
                input.focus();
            }
        }, 300);
    }, []);

    // Determine active link based on current location
    const activeLink = useMemo(() => {
        const pathname = location.pathname;

        // Special handling for store page with category path
        if (pathname === '/loja/pneus') {
            return 'tires';
        }

        // Map paths to active link identifiers
        if (pathname === '/' || pathname === '') {
            return 'index';
        }
        if (pathname === '/loja') {
            return 'store';
        }
        if (pathname.startsWith('/oficina')) {
            return 'workshop';
        }
        if (pathname === '/orcamento') {
            return 'budget';
        }

        return null;
    }, [location.pathname]);

    // Update underline position when active link changes
    useEffect(() => {
        if (!underlineRef.current) return;

        // Small delay to ensure DOM has updated after language change
        const timeoutId = setTimeout(() => {
            // Find the active link element
            let activeElement: HTMLElement | null = null;
            
            if (activeLink === 'tires') {
                activeElement = document.querySelector('.main-menu__list [data-category="tires"]') as HTMLElement;
            } else if (activeLink) {
                activeElement = document.querySelector(`.main-menu__list [data-page="${activeLink}"]:not([data-category])`) as HTMLElement;
            }

            if (!activeElement) {
                if (underlineRef.current) {
                    underlineRef.current.style.display = 'none';
                }
                activeLinkRef.current = null;
                return;
            }

            // Update underline position smoothly
            const updateUnderline = () => {
                if (!underlineRef.current || !activeElement) return;

                const linkRect = activeElement.getBoundingClientRect();
                const parentWrapper = activeElement.closest('.main-menu__list-wrapper') as HTMLElement;
                
                if (!parentWrapper) return;

                const wrapperRect = parentWrapper.getBoundingClientRect();
                
                // Calculate position relative to wrapper
                const left = linkRect.left - wrapperRect.left;
                const width = linkRect.width;
                
                // Calculate bottom position: distance from wrapper bottom to link bottom, minus 5px for offset
                // linkBottomRelativeToWrapper = linkRect.bottom - wrapperRect.top
                // distanceFromWrapperBottom = wrapperRect.height - (linkRect.bottom - wrapperRect.top)
                // bottom = distanceFromWrapperBottom - 5 (to position 5px below the link)
                const linkBottomRelativeToWrapper = linkRect.bottom - wrapperRect.top;
                const distanceFromWrapperBottom = wrapperRect.height - linkBottomRelativeToWrapper;
                const bottom = distanceFromWrapperBottom - 5; // 5px below the link

                underlineRef.current.style.display = 'block';
                underlineRef.current.style.left = `${left}px`;
                underlineRef.current.style.width = `${width}px`;
                underlineRef.current.style.bottom = `${bottom}px`;
                activeLinkRef.current = activeElement;
            };

            // Use double requestAnimationFrame for reliable positioning after DOM updates
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    updateUnderline();
                });
            });
        }, language ? 50 : 0); // Small delay when language changes to allow text reflow

        // Also update on window resize
        const handleResize = () => {
            if (!underlineRef.current || !activeLinkRef.current) return;
            
            requestAnimationFrame(() => {
                const linkRect = activeLinkRef.current!.getBoundingClientRect();
                const parentWrapper = activeLinkRef.current!.closest('.main-menu__list-wrapper') as HTMLElement;
                
                if (!parentWrapper) return;

                const wrapperRect = parentWrapper.getBoundingClientRect();
                
                // Calculate position relative to wrapper
                const left = linkRect.left - wrapperRect.left;
                const width = linkRect.width;
                
                // Calculate bottom position: distance from wrapper bottom to link bottom, minus 5px for offset
                const linkBottomRelativeToWrapper = linkRect.bottom - wrapperRect.top;
                const distanceFromWrapperBottom = wrapperRect.height - linkBottomRelativeToWrapper;
                const bottom = distanceFromWrapperBottom - 5; // 5px below the link

                underlineRef.current!.style.left = `${left}px`;
                underlineRef.current!.style.width = `${width}px`;
                underlineRef.current!.style.bottom = `${bottom}px`;
            });
        };

        window.addEventListener('resize', handleResize);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [activeLink, language]); // Re-run when active link or language changes (for text width changes)

    // Get wishlist count from localStorage
    useEffect(() => {
        const updateWishlistCount = () => {
            const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
            setWishlistCount(wishlist.length);
        };

        updateWishlistCount();
        window.addEventListener('wishlistUpdated', updateWishlistCount);
        return () => window.removeEventListener('wishlistUpdated', updateWishlistCount);
    }, []);

    // Load horario from Firestore with real-time updates
    useEffect(() => {
        const horarioQuery = query(
            getClientCollection('horario'),
            where('type', '==', 'main')
        );
        
        const unsubscribe = onSnapshot(horarioQuery, (horarioSnap) => {
            try {
                if (horarioSnap.docs.length > 0) {
                    const data = horarioSnap.docs[0].data();
                    const schedule = data.topBarSchedule?.[language] || data.topBarSchedule?.pt || t.HEADER.schedule;
                    setTopBarSchedule(schedule);
                } else {
                    // Fallback to translation
                    setTopBarSchedule(t.HEADER.schedule);
                }
            } catch (_) {
                // Fallback to translation
                setTopBarSchedule(t.HEADER.schedule);
            }
        }, () => {
            // Fallback to translation
            setTopBarSchedule(t.HEADER.schedule);
        });

        return () => unsubscribe();
    }, [language, t]);

    // Mobile scroll-based navbar show/hide
    useEffect(() => {
        const handleScroll = () => {
            // Only apply on mobile (screen width <= 768px)
            if (window.innerWidth > 768) {
                setIsNavbarVisible(true);
                return;
            }

            const currentScrollY = window.scrollY || window.pageYOffset;
            const header = document.querySelector('.main-header') as HTMLElement;
            
            if (!header) return;

            // Check if at the top
            const atTop = currentScrollY < 10;
            setIsAtTop(atTop);

            // Always show navbar at the top
            if (atTop) {
                setIsNavbarVisible(true);
                header.classList.remove('navbar-hidden');
                header.classList.add('navbar-at-top');
                lastScrollY.current = currentScrollY;
                return;
            } else {
                header.classList.remove('navbar-at-top');
            }

            // Determine scroll direction
            if (currentScrollY > lastScrollY.current) {
                // Scrolling down - hide navbar
                setIsNavbarVisible(false);
                header.classList.add('navbar-hidden');
            } else if (currentScrollY < lastScrollY.current) {
                // Scrolling up - show navbar
                setIsNavbarVisible(true);
                header.classList.remove('navbar-hidden');
            }
            // Note: Do NOT show navbar when scrolling stops - only show when scrolling up

            lastScrollY.current = currentScrollY;
        };

        // Throttle scroll events for better performance
        let ticking = false;
        const throttledHandleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledHandleScroll, { passive: true });
        
        // Initial check
        handleScroll();

        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
        };
    }, []);

    // Initialize navbar scripts
    useEffect(() => {
        navbarScript.initializeNavbar();
        navbarScript.initThemeToggle();
        
        // Expose context functions to window for sticky navbar
        (window as any).NavbarContext = {
            setLanguage: setLanguage,
            toggleColorMode: toggleColorMode,
            language: language,
            mode: mode
        };
        
        // Set up scroll listener for sticky navbar with throttling
        let scrollTimeout: NodeJS.Timeout | null = null;
        const handleScroll = () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            scrollTimeout = setTimeout(() => {
            navbarScript.initStickyNavbar();
            }, 10); // Throttle to every 10ms for smooth updates
        };
        
        // Set up language change listener to update sticky navbar
        const handleLanguageChange = () => {
            setTimeout(() => {
                navbarScript.updateStickyNavbarText();
            }, 100);
        };
        
        // Set up theme change listener to update sticky navbar
        const handleThemeChange = () => {
            setTimeout(() => {
                navbarScript.initStickyNavbar(true); // Force update
            }, 100);
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('languageChanged', handleLanguageChange);
        window.addEventListener('themeChanged', handleThemeChange);
        
        // Run sticky navbar once to check current state
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
        navbarScript.initStickyNavbar();
        });
        
        setTimeout(() => {
            navbarScript.syncMobileLanguageSwitcher();
            navbarScript.syncMobileThemeSwitcher();
            navbarScript.ensureMobileMenuWorks();
            navbarScript.ensureSearchCloseWorks();
            navbarScript.addMobileMenuButtons();
            // Setup sticky navbar handlers after content is copied
            navbarScript.setupStickyNavbarHandlers();
            // Re-check sticky navbar state after setup
            navbarScript.initStickyNavbar();
        }, 100);
        
        return () => {
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('languageChanged', handleLanguageChange);
            window.removeEventListener('themeChanged', handleThemeChange);
            delete (window as any).NavbarContext;
        };
    }, [language, mode, setLanguage, toggleColorMode]);

    // Toggle left sidebar
    const toggleLeftSidebar = () => {
        setIsLeftSidebarOpen(!isLeftSidebarOpen);
        document.body.classList.toggle('locked', !isLeftSidebarOpen);
    };

    // Close left sidebar
    const closeLeftSidebar = () => {
        setIsLeftSidebarOpen(false);
        document.body.classList.remove('locked');
    };

    // Toggle sidebar (kept for potential future use)
    // @ts-ignore - intentionally unused
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Close sidebar
    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    // Language toggle
    const handleLanguageToggle = () => {
        const newLanguage = language === 'en' ? 'pt' : 'en';
        setLanguage(newLanguage);
    };

    // Get flag and language text for toggle button (shows the language you'll switch TO)
    const langButton = getToggleLanguageInfo(language);

    // Handle sidebar form submission
    const handleSidebarFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Form submission logic can be added here
    };

    return (
        <>
            <header className={`main-header ${!isNavbarVisible ? 'navbar-hidden' : ''} ${isAtTop ? 'navbar-at-top' : ''}`}>
                {/* Top Bar */}
                <div className="main-menu__top">
                    <div className="main-menu__top-inner">
                        <ul className="list-unstyled main-menu__contact-list">
                            <li>
                                <div className="icon">
                                    <i className="icon-phone-call"></i>
                                </div>
                                <div className="text">
                                    <p>
                                        <a href={`tel:${t.CONTACT.number.replace(/[()\s-]/g, '')}`} onClick={() => trackPhoneClick('header')}>{t.CONTACT.number}</a>
                                        <span 
                                            className="info-icon phone-info-icon" 
                                            data-tooltip="phoneCallInfo"
                                            data-tooltip-text={t.CONTACT.phoneCallInfo}
                                        >
                                            <i className="fas fa-info-circle"></i>
                                        </span>
                                    </p>
                                </div>
                            </li>
                            <li>
                                <div className="icon">
                                    <i className="icon-email"></i>
                                </div>
                                <div className="text">
                                    <p><a href="mailto:geral@roadyoliveiraazemeis.com" onClick={() => trackEmailClick('header')}>geral@roadyoliveiraazemeis.com</a></p>
                                </div>
                            </li>
                            <li>
                                <div className="icon">
                                    <i className="icon-location1"></i>
                                </div>
                                <div className="text">
                                    <p>
                                        <a 
                                            href="https://www.google.com/maps/dir/?api=1&destination=Av.+D.+Maria+I+-+Lugar+de+Aldas,+3720-300+Oliveira+de+Azeméis" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="location-link"
                                            style={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <i className="fas fa-map-marker-alt" style={{ fontSize: '16px' }}></i>
                                            Av. D. Maria I - Lugar de Aldas
                                        </a>
                                    </p>
                                </div>
                            </li>
                        </ul>
                        <p className="main-menu__top-welcome-text">
                            {t.HEADER.welcome}
                        </p>
                        <div className="main-menu__top-right">
                            <div className="main-menu__top-time">
                                <div className="main-menu__top-time-icon">
                                    <span className="fas fa-clock"></span>
                                </div>
                                <p className="main-menu__top-text">
                                    {topBarSchedule || t.HEADER.schedule}
                                </p>
                            </div>
                            <div className="main-menu__social">
                                <a href="https://www.facebook.com/roady.oliveirdeazemeis" target="_blank" rel="noopener noreferrer">
                                    <i className="fab fa-facebook"></i>
                                </a>
                                <a href="https://www.instagram.com/roady.oliveiradeazemeis/" target="_blank" rel="noopener noreferrer">
                                    <i className="fab fa-instagram"></i>
                                </a>
                                <a href="https://api.whatsapp.com/send?phone=351963669277&text=Gostaria%20de%20ser%20contactado." target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('header')}>
                                    <i className="fab fa-whatsapp"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Navigation */}
                <nav className="main-menu">
                    <div className="main-menu__wrapper">
                        <div className="main-menu__wrapper-inner">
                            <div className="main-menu__left">
                                <div className="main-menu__logo">
                                    <Link to="/">
                                        <img src={logoMain} alt="Roady Oliveira de Azeméis" style={{ maxWidth: '150px', height: 'auto' }} />
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="main-menu__main-menu-box">
                                {/* Mobile: Language and Theme switches before menu button */}
                                <div className="main-menu__mobile-controls">
                                    <div className="main-menu__language-toggle main-menu__mobile-toggle">
                                        <button 
                                            id="language-switcher-mobile"
                                            onClick={handleLanguageToggle}
                                            className="language-toggle-btn" 
                                            title="Switch Language / Mudar Idioma"
                                        >
                                            <span className="flag-icon">{langButton.code === 'pt' ? (
                                                <svg className="flag-svg" viewBox="0 0 640 480" aria-hidden="true" focusable="false"><path fill="#060" d="M0 0h256v480H0z"/><path fill="#d80027" d="M256 0h384v480H256z"/><circle cx="256" cy="240" r="88" fill="#ffda44"/><circle cx="256" cy="240" r="58" fill="#fff"/><circle cx="256" cy="240" r="58" fill="none" stroke="#d80027" strokeWidth="14"/></svg>
                                            ) : (
                                                <svg className="flag-svg" viewBox="0 0 60 30" aria-hidden="true" focusable="false"><path d="M0 0v30h60V0z" fill="#012169"/><path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6"/><path d="M0 0l60 30m0-30L0 30" stroke="#C8102E" strokeWidth="3"/><path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10"/><path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6"/></svg>
                                            )}</span>
                                            <span className="language-text">{langButton.text}</span>
                                        </button>
                                    </div>
                                    <div className="main-menu__theme-toggle main-menu__mobile-toggle">
                                        <button 
                                            id="theme-toggle-mobile"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleColorMode();
                                            }}
                                            className="theme-toggle-btn" 
                                            title="Toggle Dark Mode"
                                            style={{ pointerEvents: 'auto', touchAction: 'manipulation', minWidth: '44px', minHeight: '44px' }}
                                        >
                                            <i className={mode === 'dark' ? 'fas fa-sun' : 'fas fa-moon'} id="theme-icon-mobile" style={{ pointerEvents: 'none' }}></i>
                                        </button>
                                    </div>
                                    <button 
                                        className="mobile-sidebar__toggler" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleLeftSidebar();
                                        }}
                                        role="button"
                                        aria-label="Toggle sidebar menu" 
                                        aria-expanded={isLeftSidebarOpen}
                                        style={{ pointerEvents: 'auto', touchAction: 'manipulation', minWidth: '44px', minHeight: '44px', zIndex: 1001, position: 'relative', background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}
                                    >
                                        <i className="fa fa-bars" style={{ pointerEvents: 'none', fontSize: '20px' }}></i>
                                    </button>
                                </div>
                                
                                <div className="main-menu__list-wrapper" style={{ position: 'relative' }}>
                                    {/* Underline indicator */}
                                    <div 
                                        ref={underlineRef}
                                        className="nav-underline"
                                        style={{
                                            position: 'absolute',
                                            height: '3px',
                                            backgroundColor: '#ea2127',
                                            borderRadius: '2px',
                                            transition: 'left 0.3s ease, width 0.3s ease, bottom 0.3s ease',
                                            display: 'none',
                                            pointerEvents: 'none',
                                            zIndex: 1
                                        }}
                                    />
                                <ul className="main-menu__list">
                                    <li>
                                        <Link 
                                            to={ROUTES.HOME} 
                                            className={`nav-link ${activeLink === 'index' ? 'active' : ''}`}
                                            data-page="index"
                                        >
                                            {t.NAVIGATION.start}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to={ROUTES.STORE} 
                                            className={`nav-link ${activeLink === 'store' ? 'active' : ''}`}
                                            data-page="store"
                                        >
                                            {t.NAVIGATION.store}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to={ROUTES.STORE_CATEGORY('pneus')} 
                                            className={`nav-link ${activeLink === 'tires' ? 'active' : ''}`}
                                            data-page="store" 
                                            data-category="tires"
                                        >
                                            {t.NAVIGATION.tires}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to={ROUTES.WORKSHOP} 
                                            className={`nav-link ${activeLink === 'workshop' ? 'active' : ''}`}
                                            data-page="workshop"
                                        >
                                            {t.NAVIGATION.workshop}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link 
                                            to={ROUTES.BUDGET} 
                                            className={`nav-link ${activeLink === 'budget' ? 'active' : ''}`}
                                            data-page="budget"
                                        >
                                            {t.NAVIGATION.budget}
                                        </Link>
                                    </li>
                                </ul>
                                </div>
                            </div>
                            
                            <div className="main-menu__right">
                                <div className="main-menu__call">
                                    <div className="main-menu__call-icon">
                                        <i className="icon-phone-call"></i>
                                    </div>
                                    <div className="main-menu__call-content">
                                        <Link to={ROUTES.WE_CALL} className="main-menu__call-link">
                                            <p className="main-menu__call-sub-title">{t.NAVIGATION.weCall}</p>
                                            <h5 className="main-menu__call-number">{t.NAVIGATION.weCallClick}</h5>
                                        </Link>
                                    </div>
                                </div>
                                
                                <div className="main-menu__search-cart-box">
                                    <div className="main-menu__search-cart-box">
                                        <div className="main-menu__language-toggle">
                                            <button 
                                                id="language-switcher"
                                                onClick={handleLanguageToggle}
                                                className="language-toggle-btn" 
                                                title="Switch Language / Mudar Idioma"
                                            >
                                                <span className="flag-icon">{langButton.code === 'pt' ? (
                                                <svg className="flag-svg" viewBox="0 0 640 480" aria-hidden="true" focusable="false"><path fill="#060" d="M0 0h256v480H0z"/><path fill="#d80027" d="M256 0h384v480H256z"/><circle cx="256" cy="240" r="88" fill="#ffda44"/><circle cx="256" cy="240" r="58" fill="#fff"/><circle cx="256" cy="240" r="58" fill="none" stroke="#d80027" strokeWidth="14"/></svg>
                                            ) : (
                                                <svg className="flag-svg" viewBox="0 0 60 30" aria-hidden="true" focusable="false"><path d="M0 0v30h60V0z" fill="#012169"/><path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6"/><path d="M0 0l60 30m0-30L0 30" stroke="#C8102E" strokeWidth="3"/><path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10"/><path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6"/></svg>
                                            )}</span>
                                                <span className="language-text">{langButton.text}</span>
                                            </button>
                                        </div>
                                        <div className="main-menu__theme-toggle">
                                            <button 
                                                id="theme-toggle"
                                                onClick={toggleColorMode}
                                                className="theme-toggle-btn" 
                                                title="Toggle Dark Mode"
                                            >
                                                <i className={mode === 'dark' ? 'fas fa-sun' : 'fas fa-moon'} id="theme-icon"></i>
                                            </button>
                                        </div>
                                        <div className="main-menu__search-box main-menu__desktop-only">
                                            <button 
                                                type="button"
                                                onClick={handleSearchClick}
                                                className="main-menu__search searcher-toggler-box fal fa-search"
                                                aria-label="Search"
                                            />
                                        </div>
                                        <div className="main-menu__wishlist-box main-menu__desktop-only" style={{ marginLeft: '10px' }}>
                                            <Link to="/lista-desejos" className="main-menu__wishlist">
                                                <span className="far fa-heart"></span>
                                                <span className="main-menu__wishlist-count">{wishlistCount}</span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="main-menu__btn-box main-menu__desktop-only">
                                    <Link to={ROUTES.RECRUITMENT} className="thm-btn main-menu__recruitment-btn">
                                        <span>{t.NAVIGATION.recruitment}</span>
                                        <span><i className="icon-next"></i></span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Sidebar Widget Content */}
            <div className={`xs-sidebar-group info-group info-sidebar ${isSidebarOpen ? 'active' : ''}`} ref={sidebarRef}>
                <div className="xs-overlay xs-bg-black" onClick={closeSidebar}></div>
                <div className="xs-sidebar-widget">
                    <div className="sidebar-widget-container">
                        <div className="widget-heading">
                            <a href="#" className="close-side-widget" onClick={(e) => { e.preventDefault(); closeSidebar(); }}>X</a>
                        </div>
                        <div className="sidebar-textwidget">
                            <div className="sidebar-info-contents">
                                <div className="content-inner">
                                    <div className="logo">
                                        <Link to={ROUTES.HOME} onClick={closeSidebar}>
                                            <img src={logoMain} alt="Roady Oliveira de Azeméis" style={{ maxWidth: '150px', height: 'auto' }} />
                                        </Link>
                                    </div>
                                    <div className="content-box">
                                        <h4>{t.SIDEBAR.aboutUs}</h4>
                                        <div className="inner-text">
                                            <p>{t.FOOTER.aboutText}</p>
                                        </div>
                                    </div>
                                    <div className="form-inner">
                                        <h4>{t.SIDEBAR.getQuote}</h4>
                                        <form action="/assets/inc/sendemail.php" method="POST" className="contact-form-validated" onSubmit={handleSidebarFormSubmit}>
                                            <div className="form-group">
                                                <input type="text" name="name" placeholder={t.SIDEBAR.namePlaceholder} required />
                                            </div>
                                            <div className="form-group">
                                                <input type="email" name="email" placeholder={t.SIDEBAR.emailPlaceholder} required />
                                            </div>
                                            <div className="form-group">
                                                <textarea name="message" placeholder={t.SIDEBAR.messagePlaceholder} required></textarea>
                                            </div>
                                            <div className="form-group message-btn">
                                                <button className="thm-btn" data-text={`${t.SIDEBAR.submitButton} +`} type="submit" data-loading-text={t.APPOINTMENT.pleaseWait}>
                                                    {t.SIDEBAR.submitButton}
                                                    <span className="icon-next"></span>
                                                </button>
                                            </div>
                                            <div className="result"></div>
                                        </form>
                                    </div>
                                    <div className="sidebar-contact-info">
                                        <h4>{t.SIDEBAR.contactInfo}</h4>
                                        <ul className="list-unstyled">
                                            <li>
                                                <span className="icon-pin"></span> Oliveira de Azeméis, Portugal
                                            </li>
                                            <li>
                                                <span className="icon-phone-call"></span>
                                                <a href={`tel:${t.CONTACT.number.replace(/[()\s-]/g, '')}`} onClick={() => trackPhoneClick('header')}>{t.CONTACT.number}</a>
                                                <span 
                                                    className="info-icon phone-info-icon" 
                                                    data-tooltip="phoneCallInfo"
                                                    data-tooltip-text={t.CONTACT.phoneCallInfo}
                                                >
                                                    <i className="fas fa-info-circle"></i>
                                                </span>
                                            </li>
                                            <li>
                                                <span className="icon-email"></span>
                                                <a href="mailto:geral@roadyoliveiraazemeis.com" onClick={() => trackEmailClick('header')}>geral@roadyoliveiraazemeis.com</a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="thm-social-link1">
                                        <ul className="social-box list-unstyled">
                                            <li>
                                                <a href="https://www.facebook.com/roady.oliveirdeazemeis" target="_blank" rel="noopener noreferrer">
                                                    <i className="fab fa-facebook-f" aria-hidden="true"></i>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://www.instagram.com/roady.oliveiradeazemeis/" target="_blank" rel="noopener noreferrer">
                                                    <i className="fab fa-instagram" aria-hidden="true"></i>
                                                </a>
                                            </li>
                                            <li>
                                                <a href="https://api.whatsapp.com/send?phone=351963669277&text=Gostaria%20de%20ser%20contactado." target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick('header')}>
                                                    <i className="fab fa-whatsapp" aria-hidden="true"></i>
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Left Sidebar Menu */}
            <div className={`mobile-left-sidebar ${isLeftSidebarOpen ? 'open' : ''}`}>
                <div className="mobile-left-sidebar__overlay" onClick={closeLeftSidebar}></div>
                <div className="mobile-left-sidebar__content">
                    <div className="mobile-left-sidebar__header">
                        <div className="mobile-left-sidebar__logo">
                            <Link to={ROUTES.HOME} onClick={closeLeftSidebar}>
                                <img 
                                    src={mode === 'dark' ? logoMain : logoAlternative} 
                                    alt="Roady Oliveira de Azeméis" 
                                    style={{ maxWidth: '150px', height: 'auto' }} 
                                />
                            </Link>
                        </div>
                        <button 
                            className="mobile-left-sidebar__close" 
                            onClick={closeLeftSidebar}
                            aria-label="Close sidebar"
                            style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '24px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '44px', minHeight: '44px' }}
                        >
                            <i className="fa fa-times" style={{ pointerEvents: 'none' }}></i>
                        </button>
                    </div>
                    <nav className="mobile-left-sidebar__nav">
                        <ul className="mobile-left-sidebar__list">
                        <li>
                                <Link 
                                    to={ROUTES.HOME} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${activeLink === 'index' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-home"></i>
                                    <span>{t.NAVIGATION.start}</span>
                            </Link>
                        </li>
                        <li>
                                <Link 
                                    to={ROUTES.STORE} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${activeLink === 'store' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-shopping-bag"></i>
                                    <span>{t.NAVIGATION.store}</span>
                            </Link>
                        </li>
                        <li>
                                <Link 
                                    to={ROUTES.STORE_CATEGORY('pneus')} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${activeLink === 'tires' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-circle"></i>
                                    <span>{t.NAVIGATION.tires}</span>
                            </Link>
                        </li>
                        <li>
                                <Link 
                                    to={ROUTES.WORKSHOP} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${activeLink === 'workshop' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-wrench"></i>
                                    <span>{t.NAVIGATION.workshop}</span>
                            </Link>
                        </li>
                        <li>
                                <Link 
                                    to={ROUTES.BUDGET} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${activeLink === 'budget' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-file-invoice-dollar"></i>
                                    <span>{t.NAVIGATION.budget}</span>
                            </Link>
                        </li>
                            <li>
                                <Link 
                                    to={ROUTES.WISHLIST} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${location.pathname === ROUTES.WISHLIST ? 'active' : ''}`}
                                >
                            <i className="far fa-heart"></i>
                                    <span>{language === 'pt' ? 'Lista de Desejos' : 'Wishlist'}</span>
                                    {wishlistCount > 0 && (
                                        <span className="mobile-left-sidebar__badge">{wishlistCount}</span>
                                    )}
                        </Link>
                            </li>
                            <li>
                                <Link 
                                    to={ROUTES.RECRUITMENT} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${location.pathname === ROUTES.RECRUITMENT ? 'active' : ''}`}
                                >
                                    <i className="fas fa-user-tie"></i>
                            <span>{t.NAVIGATION.recruitment}</span>
                                </Link>
                            </li>
                            <li>
                                <Link 
                                    to={ROUTES.WE_CALL} 
                                    onClick={closeLeftSidebar} 
                                    className={`mobile-left-sidebar__link ${location.pathname === ROUTES.WE_CALL ? 'active' : ''}`}
                                >
                                    <i className="icon-phone-call"></i>
                                    <span>{t.NAVIGATION.weCall}</span>
                        </Link>
                            </li>
                        </ul>
                    </nav>
                    
                    {/* Bottom Section - Contact Info and Recruitment Button */}
                    <div className="mobile-left-sidebar__footer">
                        {/* Recruitment Button */}
                        <Link 
                            to={ROUTES.RECRUITMENT} 
                            onClick={closeLeftSidebar}
                            className="mobile-left-sidebar__budget-btn"
                        >
                            <i className="fas fa-user-tie"></i>
                            <span>{t.NAVIGATION.recruitment}</span>
                        </Link>
                        
                        {/* Contact Information */}
                        <div className="mobile-left-sidebar__contact-info">
                            <div className="mobile-left-sidebar__contact-item">
                                <div className="mobile-left-sidebar__contact-icon">
                                    <i className="icon-phone-call"></i>
                                </div>
                                <div className="mobile-left-sidebar__contact-text">
                                    <a href={`tel:${t.CONTACT.number.replace(/[()\s-]/g, '')}`} onClick={() => trackPhoneClick('header')}>{t.CONTACT.number}</a>
                                </div>
                            </div>
                            
                            <div className="mobile-left-sidebar__contact-item">
                                <div className="mobile-left-sidebar__contact-icon">
                                    <i className="icon-email"></i>
                                </div>
                                <div className="mobile-left-sidebar__contact-text">
                                    <a href="mailto:geral@roadyoliveiraazemeis.com" onClick={() => trackEmailClick('header')}>geral@roadyoliveiraazemeis.com</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Header */}
            <div className="stricky-header stricked-menu main-menu">
                <div className="sticky-header__content"></div>
            </div>
        </>
    );
};

export default Navbar;

